import { useEffect, useState } from "react";
import { formatDateToCustomTable } from "../../../../utils/formatDateToCustomTable.util";
import { ContainerOptions } from "./components/ContainerOptions";
import type {
  SelectDataColumnProps,
  SelectFilterType,
} from "./select-data-column.interface";
import * as S from "./styled";

const toNumber = (v: number | string | null | undefined): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const norm = v.replace(/[^\d,-]/g, "").replace(",", ".");
    const n = Number(norm);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const isPeticionadorManual = (row: Record<string, unknown>): boolean => {
  const vaProcesso = toNumber(row?.vaProcesso as number | string | null);
  const idPeca = (row?.idPeca as number | null) ?? 0;
  return vaProcesso <= 10000 && idPeca > 0;
};

const EMPTY_FILTER_OPTION_VALUE = "__CUSTOM_TABLE_EMPTY_VALUE__";

const filterOptionCollator = new Intl.Collator("pt-BR", {
  numeric: true,
  sensitivity: "base",
});

const isEmptyTableValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return trimmedValue.length === 0 || trimmedValue === "--";
  }
  return false;
};

const hasSelectableValue = (value: unknown): boolean => {
  return !isEmptyTableValue(value);
};

const sortAlphabetically = (labels: unknown[]): unknown[] => {
  return labels.sort((a, b) =>
    filterOptionCollator.compare(String(a), String(b)),
  );
};

export const SelectDataColumn = ({
  data,
  columns,
  filterDataBySelectValueColumn,
  setFilterDataBySelectValueColumn,
}: SelectDataColumnProps) => {
  const [openModal, setOpenModal] = useState(false);

  const sortByDateDescending = (dates: any[]) => {
    return dates.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const handleData = (newValue: any, index: number) => {
    if (newValue === null) {
      const removed = [
        ...filterDataBySelectValueColumn.filter((d) => d.index !== index),
      ];
      setFilterDataBySelectValueColumn(removed);
    } else if (newValue !== null) {
      const newData = [
        ...filterDataBySelectValueColumn.filter((d) => d.index !== index),
        {
          label: newValue.value as string | number | boolean,
          keyData: newValue.position as string,
          index,
          type: newValue.type as SelectFilterType,
        },
      ];
      setFilterDataBySelectValueColumn(newData);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(!openModal);
  };

  if (filterDataBySelectValueColumn.length > 0) {
    data = (data as Record<string, unknown>[]).filter((d) => {
      return filterDataBySelectValueColumn.every((f) => {
        if (f.type === "VALUE_RANGE") {
          switch (f.label as number) {
            case 1: return +(d as any)[f.keyData] >= 0 && +(d as any)[f.keyData] <= 100;
            case 2: return +(d as any)[f.keyData] >= 100.01 && +(d as any)[f.keyData] <= 1000;
            case 3: return +(d as any)[f.keyData] >= 1000.01 && +(d as any)[f.keyData] <= 5000;
            case 4: return +(d as any)[f.keyData] >= 5000.01 && +(d as any)[f.keyData] <= 10000;
            case 5: return +(d as any)[f.keyData] >= 10000.01 && +(d as any)[f.keyData] <= 100000;
            case 6: return +(d as any)[f.keyData] >= 100000.01 && +(d as any)[f.keyData] <= 500000;
            case 7: return +(d as any)[f.keyData] > 500000;
            default: return true;
          }
        } else if (f.type === "NO_DATA") {
          return isEmptyTableValue((d as Record<string, unknown>)[f.keyData]);
        } else if (f.type === "PETICIONADOR_MANUAL") {
          return Boolean(f.label) === isPeticionadorManual(d as Record<string, unknown>);
        } else {
          return f.label === (d as Record<string, unknown>)[f.keyData];
        }
      });
    });
  }

  const handleLabelFormat = (c: any, label: any): string => {
    if (c?.formatDate) {
      return formatDateToCustomTable(label);
    }
    if (c?.customType === "PETICIONADOR_MANUAL") {

      return String(label.label);
    }
    if (c?.rangePredefinedValues) {
      return label.label;
    }
    return String(label);
  };

  return (
    <S.Wrapper>
      <S.ContainerButton onClick={handleOpenModal}>
        <S.Icon alt="Filtrar por dado de coluna." />
      </S.ContainerButton>
      <ContainerOptions
        isOpen={openModal}
        data={data}
        columns={columns}
        setOpenModal={setOpenModal}
      >
        {columns.map((c, i) => {
         
          const [selectedValue, setSelectedValue] = useState<any>(null);

          let sortedLabels: Array<any> = [];

          if (c.customType === "PETICIONADOR_MANUAL") {
            sortedLabels = [
              { label: "Sim", value: true },
              
            ];
          } else if (c?.rangePredefinedValues) {
            sortedLabels = [
              { label: "R$00,00 à R$100,00", value: 1 },
              { label: "R$100,01 à R$1000,00", value: 2 },
              { label: "R$1000,01 à R$5000,00", value: 3 },
              { label: "R$5000,01 à R$10000,00", value: 4 },
              { label: "R$10000,01 à R$100000,00", value: 5 },
              { label: "R$100000,01 à R$500000,00", value: 6 },
              { label: "Acima de R$500000,00", value: 7 },
            ];
          } else {
            const labelsSet = new Set(
              (data as Record<string, unknown>[])
                .map((d) => d[c.key])
                .filter(hasSelectableValue)
            );
            sortedLabels = Array.from(labelsSet);
            if (c?.orderByDate) {
              sortedLabels = sortByDateDescending(sortedLabels);
            } else {
              sortedLabels = sortAlphabetically(sortedLabels);
            }
          }

          const emptyValueOption =
            c.customType === "PETICIONADOR_MANUAL"
              ? []
              : [
                  {
                    label: "--",
                    value: EMPTY_FILTER_OPTION_VALUE,
                    position: c.key,
                    type: "NO_DATA" as SelectFilterType,
                  },
                ];

          useEffect(() => {
            const selectedFilter = filterDataBySelectValueColumn.find(
              (filter) => filter.index === i,
            );

            if (!selectedFilter) {
              setSelectedValue(null);
            }
          }, [filterDataBySelectValueColumn]);

          return (
            <S.ContainerSelect key={i}>
              <S.NameColum>{c.name}</S.NameColum>
              <S.CustomSelect
                isClearable={true}
                onChange={(e) => {
                  handleData(e, i);
                  setSelectedValue(e);
                }}
                value={selectedValue}
                placeholder={c.name}
                options={[
                  ...emptyValueOption,
                  ...sortedLabels.map((label: any) => ({
                    label: handleLabelFormat(c, label),
                    value:
                      c.customType === "PETICIONADOR_MANUAL"
                        ? label.value
                        : c?.rangePredefinedValues
                        ? label?.value
                        : label,
                    position: c.key,
                    type: (c.customType
                      ? c.customType
                      : c?.rangePredefinedValues
                      ? "VALUE_RANGE"
                      : "DEFAULT") as SelectFilterType,
                  })),
                ]}
              />
            </S.ContainerSelect>
          );
        })}
      </ContainerOptions>
    </S.Wrapper>
  );
};
